import { useRef } from 'react'
import { TrashDelete, Upload } from '@/assets/icons'
import Tooltip from '@/components/ui/tooltip/Tooltip'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { kAcceptedImageFormats } from '@/constants/constants-gcs'
import { OrgLogo } from './OrgLogo'

const acceptedImageMimeTypes = Object.keys(kAcceptedImageFormats).join(',')

interface EditableOrgLogoProps {
  orgName: string
  /** Present only when the org has a custom logo. Drives the "Replace" vs "Upload" affordance. */
  orgLogoUrl?: string
  /** Called with the file chosen from the OS picker. */
  onFileSelected: (file: File) => void
  /** When provided and a logo exists, a delete affordance appears on hover. */
  onDelete?: () => void
  /** Shows a spinner overlay and blocks interaction while an upload or delete is in flight. */
  isBusy?: boolean
}

/**
 * Clickable org logo tile for admins. Clicking opens the OS file picker directly (no intermediate
 * dialog); the chosen file is handed to `onFileSelected`. When a logo exists and `onDelete` is
 * provided, a small trash button appears on hover to remove it. Wraps {@link OrgLogo} with a hover
 * overlay (upload icon) and a tooltip that reads "Replace logo" when a logo exists or "Upload logo"
 * when it does not.
 */
export function EditableOrgLogo({ orgName, orgLogoUrl, onFileSelected, onDelete, isBusy = false }: EditableOrgLogoProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const label = orgLogoUrl ? 'Replace logo' : 'Upload logo'
  const showDelete = Boolean(orgLogoUrl) && Boolean(onDelete) && !isBusy

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    // Reset so picking the same file again still fires a change event.
    event.target.value = ''
    if (file) onFileSelected(file)
  }

  return (
    <>
      <div className="group relative size-12 shrink-0">
        <Tooltip title={label} placement="top" offsetY={-5} disableInteractive>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={isBusy}
            aria-label={label}
            aria-busy={isBusy}
            className="relative block size-12 rounded-lg focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            <OrgLogo orgName={orgName} orgLogoUrl={orgLogoUrl} />

            {/* Hover overlay: whitens the tile and surfaces the upload icon */}
            {!isBusy && (
              <span className="absolute inset-0 flex items-center justify-center rounded-lg border border-brand-neutral-200 bg-white/95 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                <Upload className="size-5 text-brand-neutral-800" aria-hidden />
              </span>
            )}

            {/* Busy overlay (upload or delete in flight) */}
            {isBusy && (
              <span className="absolute inset-0 flex items-center justify-center rounded-lg border border-brand-neutral-200 bg-white/95">
                <CircularProgressContinuousSized size={20} thickness={4} />
              </span>
            )}
          </button>
        </Tooltip>

        {/* Corner delete affordance — small badge tucked mostly outside the corner. On hover-capable
            devices it stays hidden and inert until the tile is hovered or the badge is focused, so
            the invisible badge never steals the tile's "Replace logo" hover. On touch (no-hover)
            devices there is no hover to reveal it, so it stays visible and tappable.

            The visible badge is 16px, but a transparent `after:` pseudo-element (`after:-inset-1.5`)
            expands the actual hit target to ~28px — above the WCAG 2.2 SC 2.5.8 24px minimum — so a
            near-miss deletes instead of falling through to the tile and opening the file picker.
            `pointer-events` is inherited, so the hit area follows the badge's hidden/revealed state. */}
        {showDelete && (
          <Tooltip title="Delete logo" placement="top" offsetY={-5} disableInteractive>
            <button
              type="button"
              onClick={onDelete}
              aria-label="Delete logo"
              className="pointer-events-auto absolute -right-2 -top-2 z-10 flex size-4 items-center justify-center rounded-full border border-brand-neutral-200 bg-white text-brand-neutral-600 opacity-100 shadow-xs transition-opacity duration-150 after:absolute after:-inset-1.5 after:content-[''] hover:text-red-600 focus:outline-hidden focus-visible:pointer-events-auto focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 hover-capable:pointer-events-none hover-capable:opacity-0 hover-capable:group-hover:pointer-events-auto hover-capable:group-hover:opacity-100"
            >
              <TrashDelete className="size-2.5" aria-hidden />
            </button>
          </Tooltip>
        )}
      </div>

      <input ref={inputRef} type="file" accept={acceptedImageMimeTypes} className="hidden" tabIndex={-1} onChange={handleChange} />
    </>
  )
}
